import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import './CreatePost.css';
import EmojiPicker from './EmojiPicker';
import BadWordsModal from './shared/BadWordsModal';

const CreatePost = ({ userName, onPostCreated }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [content, setContent] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isCompressing, setIsCompressing] = useState(false);
  const [isAnonymous, setIsAnonymous] = useState(false); // Issue #31: Anonymity toggle
  const [showEmojiPicker, setShowEmojiPicker] = useState(false); // Issue #51: Emoji picker
  const [showBadWordsModal, setShowBadWordsModal] = useState(false); // Issue #161: Bad words modal
  const [detectedBadWords, setDetectedBadWords] = useState([]); // Issue #161: Detected bad words
  const textareaRef = useRef(null); // Reference to textarea for emoji insertion

  // Issue #100: Auto-resize textarea based on content
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  }, [content]);

  const MAX_FILES = 10;
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
  const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp'];
  const MAX_IMAGE_DIMENSION = 1920; // Max width or height in pixels
  const COMPRESSION_QUALITY = 0.85; // JPEG quality (0.0 to 1.0)

  // Helper function to validate image files (checks both MIME type and extension)
  // This is important for mobile devices where MIME type might be empty or unreliable
  const isValidImageFile = (file) => {
    // Check MIME type if available
    if (file.type && ALLOWED_TYPES.includes(file.type)) {
      return true;
    }

    // Fallback: Check file extension (important for mobile devices)
    const fileName = file.name.toLowerCase();
    return ALLOWED_EXTENSIONS.some(ext => fileName.endsWith(ext));
  };

  // Compress image if it's too large
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      // If file is already small enough, don't compress
      if (file.size <= MAX_FILE_SIZE) {
        console.log('File already small enough, skipping compression:', file.size);
        resolve(file);
        return;
      }

      console.log('Compressing image:', file.name, 'Original size:', file.size);

      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // Calculate new dimensions
          let width = img.width;
          let height = img.height;

          if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
            if (width > height) {
              height = (height / width) * MAX_IMAGE_DIMENSION;
              width = MAX_IMAGE_DIMENSION;
            } else {
              width = (width / height) * MAX_IMAGE_DIMENSION;
              height = MAX_IMAGE_DIMENSION;
            }
          }

          console.log('Original dimensions:', img.width, 'x', img.height);
          console.log('New dimensions:', width, 'x', height);

          // Create canvas and compress
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                console.log('Compressed size:', blob.size);
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });

                if (compressedFile.size > MAX_FILE_SIZE) {
                  console.warn('Compressed file still too large:', compressedFile.size);
                  reject(new Error('Bild ist zu groß (auch nach Komprimierung)'));
                } else {
                  resolve(compressedFile);
                }
              } else {
                reject(new Error('Fehler beim Komprimieren des Bildes'));
              }
            },
            'image/jpeg',
            COMPRESSION_QUALITY
          );
        };
        img.onerror = () => reject(new Error('Fehler beim Laden des Bildes'));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error('Fehler beim Lesen des Bildes'));
      reader.readAsDataURL(file);
    });
  };

  const handleFileSelect = async (e) => {
    console.log('=== FILE SELECT DEBUG ===');
    console.log('Event target files:', e.target.files);
    console.log('User agent:', navigator.userAgent);

    const files = Array.from(e.target.files || []);
    console.log('Files array:', files);
    console.log('Files count:', files.length);

    setError(null);

    // Validate number of files
    if (selectedFiles.length + files.length > MAX_FILES) {
      console.log('Too many files:', selectedFiles.length + files.length);
      setError(`Maximal ${MAX_FILES} Bilder pro Post erlaubt`);
      return;
    }

    setIsCompressing(true);

    try {
      // Validate and compress each file
      const validFiles = [];
      const newPreviews = [];

      for (const file of files) {
        console.log('Processing file:', file.name, file.type, file.size);

        // Check file type (using helper function that checks both MIME type and extension)
        if (!isValidImageFile(file)) {
          console.log('Invalid file type:', file.type, 'File name:', file.name);
          setError('Nur JPEG, PNG und WebP Bilder sind erlaubt');
          setIsCompressing(false);
          return;
        }

        try {
          // Compress image if needed
          const processedFile = await compressImage(file);
          console.log('Processed file size:', processedFile.size);

          // Double-check size after compression
          if (processedFile.size > MAX_FILE_SIZE) {
            console.log('File too large even after compression:', processedFile.size);
            setError('Bild ist zu groß (max 5MB). Bitte wähle ein kleineres Bild.');
            setIsCompressing(false);
            return;
          }

          console.log('File valid, adding to validFiles');
          validFiles.push(processedFile);

          // Create preview
          const reader = new FileReader();
          reader.onload = (event) => {
            newPreviews.push({
              id: Math.random(),
              src: event.target.result,
              file: processedFile
            });

            if (newPreviews.length === validFiles.length) {
              console.log('All previews loaded, updating state');
              console.log('New previews:', newPreviews.length);
              console.log('Valid files:', validFiles.length);
              setPreviews(prev => [...prev, ...newPreviews]);
              setSelectedFiles(prev => [...prev, ...validFiles]);
              setIsCompressing(false);
            }
          };
          reader.readAsDataURL(processedFile);
        } catch (compressionError) {
          console.error('Compression error:', compressionError);
          setError(compressionError.message || 'Fehler beim Verarbeiten des Bildes');
          setIsCompressing(false);
          return;
        }
      }

      console.log('Valid files total:', validFiles.length);
    } catch (err) {
      console.error('File select error:', err);
      setError('Fehler beim Verarbeiten der Dateien');
      setIsCompressing(false);
    }

    // Reset input
    e.target.value = '';
  };

  const removeImage = (previewId) => {
    const index = previews.findIndex(p => p.id === previewId);
    if (index > -1) {
      const newPreviews = previews.filter((_, i) => i !== index);
      const newFiles = selectedFiles.filter((_, i) => i !== index);
      setPreviews(newPreviews);
      setSelectedFiles(newFiles);
    }
  };


  // Issue #51: Handle emoji selection
  const handleEmojiSelect = (emoji) => {
    if (textareaRef.current) {
      const start = textareaRef.current.selectionStart;
      const end = textareaRef.current.selectionEnd;
      const newContent = content.substring(0, start) + emoji + content.substring(end);
      setContent(newContent);
      
      // Set cursor position after emoji
      setTimeout(() => {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(start + emoji.length, start + emoji.length);
      }, 0);
    } else {
      setContent(content + emoji);
    }
    setShowEmojiPicker(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    // Validate content
    if (!content.trim()) {
      setError('Beitrag kann nicht leer sein');
      return;
    }

    console.log('=== CREATE POST DEBUG ===');
    console.log('Content:', content);
    console.log('Selected files count:', selectedFiles.length);
    console.log('Selected files:', selectedFiles);
    console.log('API Base URL:', process.env.REACT_APP_API_BASE_URL);

    setIsSubmitting(true);

    try {
      // Create FormData for multipart request
      const formData = new FormData();
      formData.append('content', content);
      formData.append('is_anonymous', isAnonymous); // Issue #31: Send anonymity setting

      // Add files to FormData
      selectedFiles.forEach((file, index) => {
        console.log(`Adding file ${index}:`, file.name, file.type, file.size);
        formData.append(`images`, file);
      });

      console.log('FormData entries:');
      for (let pair of formData.entries()) {
        console.log(pair[0], pair[1]);
      }

      // Submit post
      console.log('Submitting to /api/posts...');
      const response = await axios.post('/api/posts', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('Response:', response.data);

      // Show success message
      setSuccessMessage('Post erfolgreich erstellt!');

      // Reset form
      setContent('');
      setSelectedFiles([]);
      setPreviews([]);
      setIsAnonymous(false); // Issue #31: Reset anonymity toggle
      setIsOpen(false);

      // Notify parent component
      if (onPostCreated) {
        onPostCreated(response.data);
      }

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('=== ERROR creating post ===');
      console.error('Full error:', err);
      console.error('Error message:', err.message);
      console.error('Response data:', err.response?.data);
      console.error('Response status:', err.response?.status);
      console.error('Response headers:', err.response?.headers);
      console.error('Request config:', err.config);

      let errorMsg = 'Fehler beim Erstellen des Posts';

      if (err.response) {
        // Issue #161: Check if bad words were detected - show modal instead of text error
        const badWords = err.response?.data?.bad_words;
        if (badWords && badWords.length > 0) {
          setDetectedBadWords(badWords);
          setShowBadWordsModal(true);
          // Don't set error text - modal handles the display
        } else {
          // Server responded with error - prioritize server message
          const serverMessage = err.response?.data?.message;

          if (serverMessage) {
            errorMsg = serverMessage;
          } else if (err.response.status === 413) {
            errorMsg = 'Datei ist zu groß. Bitte wähle kleinere Bilder.';
          } else if (err.response.status === 422) {
            errorMsg = 'Ungültige Daten. Bitte überprüfe deine Eingabe.';
          } else if (err.response.status === 401) {
            errorMsg = 'Sitzung abgelaufen. Bitte melde dich erneut an.';
          } else {
            errorMsg = `Server Fehler (${err.response.status})`;
          }
          setError(errorMsg);
        }
      } else if (err.request) {
        // Request made but no response
        errorMsg = 'Keine Verbindung zum Server. Bitte überprüfe deine Internetverbindung.';
        console.error('No response received from server');
        setError(errorMsg);
      } else {
        // Something else went wrong
        errorMsg = err.message || 'Unbekannter Fehler beim Erstellen des Posts';
        setError(errorMsg);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) {
    return (
      <div className="create-post-bar-wrapper">
        <button className="create-post-button create-post-trigger" onClick={() => setIsOpen(true)}>
          <span className="edit-icon">✏️</span>
          <span className="create-post-text">
            Hallo {userName}, teile jetzt deine Gedanken
          </span>
        </button>
      </div>
    );
  }

  return (
    <div className="create-post-expanded-wrapper">
      {/* Issue #161: Bad Words Modal */}
      <BadWordsModal
        isOpen={showBadWordsModal}
        onClose={() => setShowBadWordsModal(false)}
        badWords={detectedBadWords}
        contentType="post"
      />

      {/* Error Message */}
      {error && <div className="error-message">{error}</div>}

      {/* Success Message */}
      {successMessage && <div className="success-message">{successMessage}</div>}

      <form onSubmit={handleSubmit} className="create-post-form">
        {/* Text Field with Icon */}
        {/* Issue #262: Removed autoFocus to prevent keyboard from appearing on mobile before user taps input */}
        <div className="text-field-container">
          <span className="chat-icon">💬</span>
          <textarea
            ref={textareaRef}
            className="post-content-input"
            placeholder="Deine Gedanken.."
            value={content}
            onChange={(e) => setContent(e.target.value)}
            aria-label="Deine Gedanken"
          />
        </div>

        {/* Image Previews */}
        {previews.length > 0 && (
          <div className="image-previews">
            {previews.map(preview => (
              <div key={preview.id} className="preview-item">
                <img src={preview.src} alt="Preview" className="preview-image" />
                <button
                  type="button"
                  className="remove-image-button"
                  onClick={() => removeImage(preview.id)}
                  title="Bild entfernen"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Action Buttons */}
        <div className="action-buttons-row">
          <div className="left-buttons">
            {/* Issue #31: Anonymity toggle */}
            <label className="anonymity-toggle" title="Anonym posten">
              <input
                type="checkbox"
                checked={isAnonymous}
                onChange={(e) => setIsAnonymous(e.target.checked)}
                disabled={isSubmitting}
              />
              <span className="toggle-switch"></span>
              <span className="toggle-label">Anonym</span>
            </label>
            <button
              type="button"
              className="icon-button emoji-button"
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              title="Emoji hinzufügen"
            >
              😊
            </button>
            <label className="icon-button camera-button" title={isCompressing ? "Komprimiere..." : "Bilder hinzufügen"}>
              {isCompressing ? '⏳' : '📷'}
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileSelect}
                disabled={selectedFiles.length >= MAX_FILES || isSubmitting || isCompressing}
                className="file-input"
              />
            </label>
          </div>
          <button
            type="submit"
            className="icon-button send-button"
            disabled={!content.trim() || isSubmitting}
            title="Posten"
          >
            {isSubmitting ? '⏳' : '➤'}
          </button>
        </div>
      </form>

      {/* Issue #51: Emoji Picker */}
      {showEmojiPicker && (
        <EmojiPicker
          onEmojiSelect={handleEmojiSelect}
          onClose={() => setShowEmojiPicker(false)}
        />
      )}
    </div>
  );
};

export default CreatePost;
